import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, BookOpen, Sparkles, Quote, Linkedin, Newspaper, ExternalLink } from "lucide-react";
import RebrandReveal from "@/components/RebrandReveal";
import WaitlistForm from "@/components/WaitlistForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEW_LOGO = "/logos/entrepreneur-stories.png";
const OLD_LOGO = "/logos/self-employed.png";

export default function Landing() {
  const [revealComplete, setRevealComplete] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleSubmit = async (email) => {
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      setJoined(true);
      toast.success("You're on the list.", {
        description: `Welcome aboard — you're #${res.data.position}.`,
      });
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
      return false;
    }
  };

  return (
    <main data-testid="landing-page" className="relative min-h-screen overflow-hidden">
      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-zinc-500 font-sans">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EA580C]" />
          The Rebrand · 2026
        </div>
        <a
          href="#waitlist"
          data-testid="header-join-link"
          className="text-xs tracking-[0.25em] uppercase text-zinc-700 hover:text-[#EA580C] transition-colors font-sans"
        >
          Join the waitlist
        </a>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-8 md:pt-16 pb-24">
        <div className="max-w-5xl mx-auto">
          <RebrandReveal
            oldLogo={OLD_LOGO}
            newLogo={NEW_LOGO}
            onComplete={() => setRevealComplete(true)}
          />

          <AnimatePresence>
            {revealComplete && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="mt-12 md:mt-16 max-w-3xl"
              >
                <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6 font-sans">
                  An announcement
                </div>

                <h1
                  data-testid="hero-heading"
                  className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight text-zinc-900"
                >
                  <span className="text-zinc-400 line-through decoration-[#EA580C]/60 decoration-[3px]">
                    Self-Employed
                  </span>{" "}
                  <span className="italic">is now</span>
                  <br />
                  Entrepreneur Stories.
                </h1>

                <p className="mt-8 text-base md:text-lg text-zinc-600 max-w-xl leading-relaxed font-sans">
                  Same obsession. New chapter. A weekly newsletter for the people
                  building things — told through the unfiltered stories of the
                  founders living it.
                </p>

                <div id="waitlist" className="mt-12 max-w-md">
                  <WaitlistForm onSubmit={handleSubmit} joined={joined} />
                </div>

                <div className="mt-6 text-xs tracking-widest uppercase text-zinc-400 font-sans">
                  No spam. One story. Every Sunday.
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* What to expect */}
      <section className="relative z-10 px-6 md:px-12 py-20 md:py-28 border-t border-zinc-200/70">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 font-sans">
                What to expect
              </div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-zinc-900 max-w-xl leading-[1.05]">
                Real stories. <span className="italic text-zinc-500">Real founders.</span>
              </h2>
            </div>
            <div className="text-sm text-zinc-500 max-w-xs font-sans leading-relaxed">
              Curated, long-form, and refreshingly free of LinkedIn-flavored
              advice.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FeatureCard
              icon={<BookOpen className="w-5 h-5" />}
              kicker="01 · Weekly"
              title="One story, deeply told"
              body="A single founder, a single chapter. The wins, the spirals, the things they wouldn't say on a podcast."
            />
            <FeatureCard
              icon={<Quote className="w-5 h-5" />}
              kicker="02 · Verbatim"
              title="In their own words"
              body="Long-form interviews edited for flow, not for sanitization. You'll hear them think."
              accent
            />
            <FeatureCard
              icon={<Sparkles className="w-5 h-5" />}
              kicker="03 · Useful"
              title="Lessons you can steal"
              body="Every issue ends with three tactical takeaways. Save them. Use them. Steal shamelessly."
            />
          </div>
        </div>
      </section>

      {/* From → To rebrand strip */}
      <section className="relative z-10 px-6 md:px-12 py-20 md:py-28 border-t border-zinc-200/70">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4 font-sans">
                The rebrand
              </div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-zinc-900 max-w-2xl leading-[1.05]">
                A new chapter, <span className="italic text-zinc-500">side by side.</span>
              </h2>
            </div>
            <div className="text-sm text-zinc-500 max-w-xs font-sans leading-relaxed">
              The same people, the same mission — with a sharper editorial voice
              built for what comes next.
            </div>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-4 items-stretch">
            {/* BEFORE card */}
            <div className="relative bg-white border border-zinc-200/70 p-8 md:p-10 flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-400 font-sans">
                  Before
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center py-6">
                <div className="w-44 h-44 md:w-52 md:h-52 overflow-hidden shadow-[0_30px_60px_-30px_rgba(234,88,12,0.5)] ring-1 ring-black/5">
                  <img
                    src={OLD_LOGO}
                    alt="Self-Employed (old logo)"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-zinc-100">
                <div className="font-serif text-2xl text-zinc-900">Self-Employed</div>
                <p className="text-sm text-zinc-500 mt-2 font-sans leading-relaxed">
                  The handshake. The hustle. The brand that started it all.
                </p>
                <a
                  data-testid="old-linkedin-link"
                  href="https://www.linkedin.com/company/indpendent-contractor/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 text-xs tracking-[0.2em] uppercase text-zinc-700 hover:bg-zinc-950 hover:text-white hover:border-zinc-950 transition-colors font-sans"
                >
                  <Linkedin className="w-3.5 h-3.5" />
                  Visit LinkedIn
                </a>
              </div>
            </div>

            {/* CONNECTOR */}
            <div className="flex md:flex-col items-center justify-center gap-3 py-4 md:py-0 md:px-2">
              <div className="hidden md:block h-1/3 w-px bg-gradient-to-b from-transparent to-zinc-300" />
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#EA580C] text-white shadow-[0_10px_30px_-10px_rgba(234,88,12,0.6)] rotate-90 md:rotate-0">
                <ArrowRight className="w-5 h-5" />
              </div>
              <div className="font-serif italic text-sm text-zinc-500 md:my-1">
                is now
              </div>
              <div className="hidden md:block h-1/3 w-px bg-gradient-to-b from-zinc-300 to-transparent" />
            </div>

            {/* NOW card */}
            <div className="relative bg-zinc-950 text-white p-8 md:p-10 flex flex-col">
              <div className="flex items-center justify-between mb-8">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#EA580C] font-sans">
                  Now
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center py-6">
                <div className="w-44 h-44 md:w-52 md:h-52 bg-white flex items-center justify-center ring-1 ring-white/10">
                  <img
                    src={NEW_LOGO}
                    alt="Entrepreneur Stories (new logo)"
                    className="w-[80%] h-[80%] object-contain"
                  />
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="font-serif text-2xl">Entrepreneur Stories</div>
                <p className="text-sm text-zinc-400 mt-2 font-sans leading-relaxed">
                  Same heart. Sharper voice. A newsletter for the people
                  actually doing it.
                </p>
                <a
                  data-testid="new-linkedin-link"
                  href="https://www.linkedin.com/company/entrepreneurstories/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs tracking-[0.2em] uppercase text-white hover:bg-[#EA580C] hover:border-[#EA580C] transition-colors font-sans"
                >
                  <Linkedin className="w-3.5 h-3.5" />
                  Visit LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press release */}
      <section className="relative z-10 px-6 md:px-12 py-20 md:py-24 border-t border-zinc-200/70 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-3">
              <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 mb-3 font-sans">
                In the press
              </div>
              <div className="w-12 h-12 flex items-center justify-center border border-zinc-200 text-zinc-700">
                <Newspaper className="w-5 h-5" />
              </div>
            </div>
            <div className="md:col-span-9">
              <h3 className="font-serif text-2xl md:text-4xl tracking-tight text-zinc-900 leading-[1.15]">
                <span className="italic text-zinc-500">"Self-Employed announces rebrand to Entrepreneur Stories,</span>{" "}
                marking a new editorial direction."
              </h3>
              <p className="mt-5 text-zinc-600 font-sans text-sm md:text-base leading-relaxed max-w-2xl">
                Read the official press release covering the full story behind the
                rebrand — what's changing, what's staying, and where we're headed
                next.
              </p>
              <a
                data-testid="press-release-link"
                href="https://www.issuewire.com/self-employed-announces-rebrand-to-entrepreneur-stories-marking-a-new-editorial-direction-1868841125011430"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center gap-2 px-5 py-3 bg-zinc-950 text-white text-sm tracking-wide hover:bg-[#EA580C] transition-colors font-sans"
              >
                Read the press release
                <ExternalLink className="w-4 h-4" />
              </a>
              <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-sans">
                Source · IssueWire
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 md:px-12 py-24 md:py-32 border-t border-zinc-200/70">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-serif text-4xl md:text-6xl tracking-tight text-zinc-900 leading-[1.05]">
            Be there for <span className="italic">issue 001.</span>
          </h3>
          <p className="mt-6 text-zinc-600 font-sans">
            The first story drops the week we launch. Get it before anyone else.
          </p>

          <div className="mt-10 max-w-md mx-auto">
            <WaitlistForm onSubmit={handleSubmit} joined={joined} testIdPrefix="footer" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 px-6 md:px-12 py-10 border-t border-zinc-200/70">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs tracking-[0.2em] uppercase text-zinc-400 font-sans">
          <div>© 2026 Entrepreneur Stories</div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#EA580C]" />
            <span>Formerly</span>
            <a
              data-testid="footer-linkedin-link"
              href="https://www.linkedin.com/company/indpendent-contractor/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline decoration-zinc-300 hover:decoration-[#EA580C] hover:text-[#EA580C] transition-colors"
            >
              Self-Employed
              <Linkedin className="w-3 h-3" />
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}

function FeatureCard({ icon, kicker, title, body, accent = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`group p-8 border ${
        accent
          ? "bg-zinc-950 text-white border-zinc-950"
          : "bg-white border-zinc-200/70"
      } transition-shadow hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]`}
    >
      <div
        className={`w-10 h-10 flex items-center justify-center border ${
          accent ? "border-white/15 text-white" : "border-zinc-200 text-zinc-700"
        } mb-6 transition-colors group-hover:border-[#EA580C] group-hover:text-[#EA580C]`}
      >
        {icon}
      </div>
      <div
        className={`text-[10px] tracking-[0.3em] uppercase mb-3 font-sans ${
          accent ? "text-zinc-500" : "text-zinc-400"
        }`}
      >
        {kicker}
      </div>
      <h3 className="font-serif text-2xl tracking-tight leading-tight">{title}</h3>
      <p
        className={`mt-3 text-sm leading-relaxed font-sans ${
          accent ? "text-zinc-400" : "text-zinc-600"
        }`}
      >
        {body}
      </p>
    </motion.div>
  );
}
